import * as Yup from 'yup';
import { CuffaManagementTypeEnum, CuffaAgroecologicalCertificationEnum, CuffaProductionTypeEnum } from 'common/enums';

export const months: { key: string; label: string }[] = [
  { key: 'january', label: 'Jan' },
  { key: 'february', label: 'Fev' },
  { key: 'march', label: 'Mar' },
  { key: 'april', label: 'Abr' },
  { key: 'may', label: 'Mai' },
  { key: 'june', label: 'Jun' },
  { key: 'july', label: 'Jul' },
  { key: 'august', label: 'Ago' },
  { key: 'september', label: 'Set' },
  { key: 'october', label: 'Out' },
  { key: 'november', label: 'Nov' },
  { key: 'december', label: 'Dez' },
];

export const initialValues = {
  productionTypes: [],
  selectedProducts: [],
  managementType: '',
  area_hectares: '',
  volume_produced: '',
  volume_commercialized: '',
  commercialized_value: '',
  volume_consumed_donated: '',
  volume_lost: '',
  availability_months: {},
  has_certification: null,
  certification_type: '',
  other_certification_name: '',
};

export const validationSchema = Yup.object().shape({
  productionTypes: Yup.array()
    .of(Yup.mixed<CuffaProductionTypeEnum>().oneOf(Object.values(CuffaProductionTypeEnum)))
    .min(1, 'Selecione pelo menos um tipo de produção')
    .required('Este campo é obrigatório'),

  selectedProducts: Yup.array()
    .min(1, 'Selecione pelo menos um produto')
    .of(
      Yup.object().shape({
        name: Yup.string().required('Nome do produto é obrigatório'),
        quantity: Yup.number()
          .min(0.01, 'Quantidade deve ser maior que 0')
          .required('Quantidade é obrigatória'),
        unit: Yup.string().required('Unidade é obrigatória')
      })
    ),

  managementType: Yup.string()
    .required('Selecione um tipo de manejo/produção'),

  area_hectares: Yup.number()
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.min(0.01, 'Área deve ser maior que 0').required('Área é obrigatória'),
      otherwise: (schema) => schema.nullable()
    }),

  volume_produced: Yup.number()
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.min(0.01, 'Volume deve ser maior que 0').required('Volume produzido é obrigatório'),
      otherwise: (schema) => schema.nullable()
    }),

  volume_commercialized: Yup.number()
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.min(0, 'Volume não pode ser negativo'),
      otherwise: (schema) => schema.nullable()
    }),

  volume_consumed_donated: Yup.number()
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.min(0, 'Volume não pode ser negativo'),
      otherwise: (schema) => schema.nullable()
    }),

  volume_lost: Yup.number()
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.min(0, 'Volume não pode ser negativo'),
      otherwise: (schema) => schema.nullable()
    }),

  availability_months: Yup.object()
    .test('at-least-one-month', 'Selecione pelo menos um mês ou "Disponível o ano todo"', function (value) {
      if (!value) return false;
      const { managementType } = this.parent;
      if (!managementType) return true;
      const hasMonthSelected = value.available_year_round ||
        months.some(month => value[month.key]);
      return hasMonthSelected;
    }),

  has_certification: Yup.boolean()
    .nullable()
    .typeError('Selecione Sim ou Não')
    .when('managementType', {
      is: (val: string) => [CuffaManagementTypeEnum.AGROECOLOGICA, CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF].includes(val as CuffaManagementTypeEnum),
      then: (schema) => schema.required('Este campo é obrigatório'),
      otherwise: (schema) => schema.nullable()
    }),

  certification_type: Yup.string()
    .when('has_certification', {
      is: true,
      then: (schema) => schema.required('Selecione o tipo de certificação'),
      otherwise: (schema) => schema.nullable()
    }),

  other_certification_name: Yup.string()
    .when('certification_type', {
      is: CuffaAgroecologicalCertificationEnum.OUTRO,
      then: (schema) => schema.required('Especifique o tipo de certificação'),
      otherwise: (schema) => schema.nullable()
    }),
});

export default validationSchema;
